import api from '@/lib/services/general.service'

// Connected-integrations status, read live from the BFF (/v1/user/integrations), which asks
// the IDS token store — the single source of truth. `loaded` matters: "unknown" must NOT be
// treated as "disconnected" (the inbox gates on it), so consumers only act when loaded=true.
export const state = {
  google: { connected: false, email: null },
  // Does the Google grant include Drive (doc-attach, 2026-10-02)? The IDS's /Providers entry says
  // `driveGranted`; anything it does not say — unknown, a legacy entry, the BFF not passing it — is
  // TRUE, so the attach control only warns when the grant is known to lack the scope.
  driveGranted: true,
  // busy-ids#13 — Google's refresh token was revoked or expired (invalid_grant). The BFF then
  // reports `connected: false` + `needsReconnect: true` (email kept), so every "is Google
  // connected?" check already treats it as not connected; this only changes the wording to
  // "Reconnect Google". Older BFFs omit the field → false.
  needsReconnect: false,
  needsReconnectReason: null,
  loaded: false,
  loading: false,
}

export const getters = {
  google: s => s.google,
  googleConnected: s => s.google.connected,
  googleEmail: s => s.google.email,
  driveGranted: s => s.driveGranted !== false,
  googleNeedsReconnect: s => !!s.needsReconnect && !s.google.connected,
  googleNeedsReconnectReason: s => s.needsReconnectReason,
  loaded: s => s.loaded,
}

export const actions = {
  async fetchStatus({ commit, state, rootGetters }, { force = false } = {}) {
    if (!rootGetters['user/isAuthenticated']) return
    if (state.loading || (state.loaded && !force)) return
    commit('SET_LOADING', true)
    try {
      const res = await api.get('user/integrations', {})
      const g = res?.data?.google || {}
      commit('SET_GOOGLE', { connected: !!g.connected, email: g.email || null })
      commit('SET_DRIVE_GRANTED', g.driveGranted !== false)
      commit('SET_NEEDS_RECONNECT', { needsReconnect: g.needsReconnect === true, reason: g.needsReconnectReason || null })
      commit('SET_LOADED', true)
    } catch {
      // Status unavailable (503 during an IDS hiccup) — stay "unknown", don't gate the inbox.
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async disconnectGoogle({ commit }) {
    await api.deleteWithoutId('user/integrations/google')
    commit('SET_GOOGLE', { connected: false, email: null })
    commit('SET_NEEDS_RECONNECT', { needsReconnect: false, reason: null })
    commit('SET_LOADED', true)
  },
}

export const mutations = {
  SET_GOOGLE(s, val) {
    s.google = val
  },
  SET_DRIVE_GRANTED(s, val) {
    s.driveGranted = val !== false
  },
  SET_NEEDS_RECONNECT(s, { needsReconnect, reason } = {}) {
    s.needsReconnect = needsReconnect === true
    s.needsReconnectReason = s.needsReconnect ? reason || null : null
  },
  SET_LOADED(s, val) {
    s.loaded = val
  },
  SET_LOADING(s, val) {
    s.loading = val
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}
